import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { PersonDto, Version } from 'src/app/api/models';
import { AppState } from 'src/app/store/states/app.state';
import { LocalStorageData, LocalStorageUserBookData, LocalStorageUserData } from '../models/local-storage.model';
import * as fromUserSelectors from 'src/app/store/selectors/user.selector';
import { Languages } from '../translation/languages.enum';


const STORAGE_KEY = 'DIANA_USER_DATA';
const VERSION_KEY = 'DIANA_VERSION';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  currentUser?: PersonDto | null;

  constructor(private store$: Store<AppState>) {
    this.store$
      .select(fromUserSelectors.getCurrentUser)
      .subscribe((user) => (this.currentUser = user));
  }

  getSelectedBookData(): LocalStorageUserBookData | null {
    return this._getBookData();
  }

  getBookData(bookId: string): LocalStorageUserBookData | null {
    return this._getBookData(bookId);
  }

  getAllBookData(): LocalStorageUserBookData[] | null {
    return this.getUserData()?.books || null;
  }

  getLanguage(): Languages | null {
    return this.getCompleteUserData()?.language || null;
  }

  setLanguage(language: Languages): void {
    const completeUserData = this.getCompleteUserData();
    if (!completeUserData && this.currentUser) {
      const newUserData = {
        userId: this.currentUser.dcUserId,
        language,
        data: { [this.currentUser.currentOrgUnitId]: this.initialState() },
      };
      this.setCompleteUserData(newUserData);
    } else if (completeUserData) {
      completeUserData.language = language;
      this.setCompleteUserData(completeUserData);
    }
  }

  setBookData(bookId: string, selectedResourceIds: string[] | null): void {

    const userData = this.getUserData() || this.initialState();

    if (selectedResourceIds != null) {
      const bookData: LocalStorageUserBookData = {
        bookId,
        selectedResourceIds: selectedResourceIds,
      };
      const existingBookIndex = userData.books.findIndex((b) => b.bookId === bookId);
      if (existingBookIndex >= 0) {
        userData.books[existingBookIndex] = bookData;
      } else {
        userData.books.push(bookData);
      }
    }

    userData.selectedBookId = bookId;

    this.setUserData(userData);
  }

  getVersion(): Version {
    const data = localStorage.getItem(VERSION_KEY);
    return data ? JSON.parse(data) : {};
  }

  setVersion(version: Version): void {
    localStorage.setItem(VERSION_KEY, JSON.stringify(version));
  }

  private initialState(): LocalStorageUserData {
    return { books: [], selectedBookId: null };
  }

  private setData(data: LocalStorageData[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  private getData(): LocalStorageData[] {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  }

  private getCompleteUserData(): LocalStorageData | null {
    if (!this.currentUser) return null;

    const data = this.getData();
    return data.find((d) => d.userId === this.currentUser?.dcUserId) || null;
  }

  private getUserData(): LocalStorageUserData {
    const completeUserData = this.getCompleteUserData();
    const orgUnitId = this.currentUser?.currentOrgUnitId;

    if (completeUserData && orgUnitId && completeUserData.data[orgUnitId]) {
      return completeUserData.data[orgUnitId];
    }
    return this.initialState();
  }

  private _getBookData(bookId: string | null = null): LocalStorageUserBookData | null {
    const userData = this.getUserData();
    const targetBookId = bookId || userData.selectedBookId;

    return userData.books.find((b) => b.bookId === targetBookId) || null;
  }

  private setUserData(userData: LocalStorageUserData): void {
    if (!this.currentUser) return;

    const data = this.getData();
    const userIndex = data.findIndex((d) => d.userId === this.currentUser?.dcUserId);
    const orgUnitId = this.currentUser.currentOrgUnitId;

    if (userIndex >= 0) {
      data[userIndex].data[orgUnitId] = userData;
    } else {
      const newUserData: LocalStorageData = {
        userId: this.currentUser.dcUserId,
        data: { [orgUnitId]: userData },
        language: null,
      };
      data.push(newUserData);
    }

    this.setData(data);
  }

  private setCompleteUserData(userData: LocalStorageData): void {
    if (!this.currentUser) return;

    const data = this.getData();
    const userIndex = data.findIndex((d) => d.userId === this.currentUser?.dcUserId);

    if (userIndex >= 0) {
      data[userIndex] = userData;
    } else {
      data.push(userData);
    }

    this.setData(data);
  }
}